import { Popover } from "@kobalte/core/popover";
import { dispatchGlobalEvent } from "@storytell/events";
import type { Asset, AssetId, Concept, ConceptId, Label, LabelId, View, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { TbAt, TbX } from "solid-icons/tb";
import { type Component, Show, createSignal } from "solid-js";
import { twMerge } from "tailwind-merge";
import { ProjectSwitcherModal } from "~/components/projects/ProjectSwitcherModal";
import { useGateActionByAuthenticated } from "~/hooks/useGateActionByAuthenticated";
import { useWire } from "~/wire";
import { ScopedItemsList } from "./ScopedItemsList";

export const ScopeDisplay: Component = () => {
  const wire = useWire();
  const {
    chat: { scope },
    knowledgeUi,
  } = wire;
  const assets = () =>
    scope
      .assets()
      .map((a) => wire.assets.get(a as AssetId))
      .filter((a) => a !== undefined) as Asset[];
  const concepts = () =>
    scope
      .concepts()
      .map((c) => wire.concepts.get(c as ConceptId))
      .filter((c) => c !== undefined) as Concept[];
  const labels = () =>
    scope
      .labels()
      .map((l) => wire.labels.get(l as LabelId))
      .filter((l) => l !== undefined) as Label[];
  const views = () =>
    scope
      .views()
      .map((v) => wire.views.get(v as ViewId))
      .filter((v) => v !== undefined) as View[];

  const totalItems = () => assets().length + concepts().length + labels().length + views().length;

  const activeProject = () => {
    const userContext = wire.userContext.get();
    if (!userContext) return undefined;
    return wire.projects.get(userContext.projectId);
  };

  const [isProjectModalOpen, setIsProjectModalOpen] = createSignal(false);
  const openProjectModal = useGateActionByAuthenticated(
    () => ({
      title: "Sign up to switch projects",
      description: "Projects are a great way to collaborate with your team.",
    }),
    () => setIsProjectModalOpen(true),
  );

  return (
    <div class="flex items-center gap-2 text-xs px-3 mb-2 text-secondary-text">
      <Show
        when={wire.assets.searchIds("").length}
        fallback={
          <StPopover
            placement="top"
            content={
              <div class="flex flex-col gap-2 p-4 justify-center items-center">
                <p>Upload files to chat with</p>
                <Popover.CloseButton as={StButton} onClick={() => dispatchGlobalEvent("open-file-picker", undefined)}>
                  Upload Files
                </Popover.CloseButton>
              </div>
            }
          >
            <StPopoverTrigger
              as="button"
              type="button"
              class={twMerge(
                "group/btn shrink-0 flex bg-canvas dark:bg-tile/50 dark:hover:bg-tile-hover/50 hover:bg-canvas-hover rounded-full border border-muted-border hover:border-primary-border dark:border-white/10 dark:hover:border-white/10 items-center gap-1 py-1 px-2 cursor-pointer select-none",
                scope.allKnowledge() && "bg-brand hover:bg-brand-hover text-on-brand",
              )}
            >
              <TbAt
                class={twMerge(
                  "size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text",
                  scope.allKnowledge() && "text-on-brand opacity-80",
                )}
              />
              <span>Chat with</span>
            </StPopoverTrigger>
          </StPopover>
        }
      >
        <Show
          when={scope.allKnowledge()}
          fallback={
            <>
              <Show when={totalItems() > 0}>
                <span class="text-secondary-text shrink-0">Chatting with</span>
              </Show>
              <Show when={totalItems() === 0}>
                <span
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      knowledgeUi.open();
                    }
                  }}
                  onClick={() => knowledgeUi.open()}
                  class="group/btn shrink-0 flex bg-canvas dark:bg-tile/50 dark:hover:bg-tile-hover/50 hover:bg-canvas-hover rounded-full border border-muted-border hover:border-primary-border dark:border-white/10 dark:hover:border-white/10 items-center gap-1 py-1 px-2 cursor-pointer select-none"
                >
                  <TbAt class="size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
                  <span>Chat with</span>
                </span>
              </Show>
            </>
          }
        >
          <span class="text-secondary-text">Chatting with</span>
          <span
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                knowledgeUi.open();
              }
            }}
            onClick={() => knowledgeUi.open()}
            class="group/btn shrink-0 flex bg-brand hover:bg-brand-hover text-on-brand rounded-full border border-brand items-center gap-1 py-1 px-2 cursor-pointer select-none"
          >
            <TbAt class="size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-on-brand opacity-80" />
            <span>All Knowledge</span>
            <button
              type="button"
              title="Remove all knowledge from scope"
              aria-label="Remove all knowledge from scope"
              onClick={(e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                scope.setAllKnowledge(false);
              }}
            >
              <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
            </button>
          </span>
        </Show>
      </Show>

      <div class="flex items-center gap-2 flex-auto max-w-full">
        <Show when={!scope.allKnowledge() && totalItems() > 0}>
          <ScopedItemsList
            views={views}
            labels={labels}
            concepts={concepts}
            assets={assets}
            uploadingAssets={scope.uploadingAssets}
            allowRemove
            onRemoveView={(id) => scope.removeView(id as ViewId)}
            onRemoveLabel={(id) => scope.removeLabel(id as LabelId)}
            onRemoveConcept={(id) => scope.removeConcept(id as ConceptId)}
            onRemoveAsset={(id) => scope.removeAsset(id as AssetId)}
          />
        </Show>

        <Show when={(scope.allKnowledge() || totalItems() > 0) && activeProject()?.name}>
          <span class="text-secondary-text shrink-0">
            in{" "}
            <button
              type="button"
              onClick={openProjectModal}
              class="text-secondary-text hover:text-primary-text cursor-pointer transition-colors"
            >
              {activeProject()?.name}
            </button>
          </span>
        </Show>

        <Show when={!scope.allKnowledge() && totalItems() > 1}>
          <button
            type="button"
            onClick={scope.clearAll}
            class="text-xs shrink-0 text-muted-text hover:text-secondary-text transition-colors underline cursor-pointer select-none"
            title="Clear all items from scope"
          >
            Clear all
          </button>
        </Show>
      </div>

      <ProjectSwitcherModal open={isProjectModalOpen()} onOpenChange={setIsProjectModalOpen} />
    </div>
  );
};
